import { Image } from "@booking/components/atoms/media/Image";
import { type ImageProps } from "@booking/components/atoms/media/Image";
import { IMAGES_METRICS } from "@booking/consts";
import { ImageProviderEnum, ImageVariantEnum } from "@booking/enums";
import { useWindowSize } from "@uidotdev/usehooks";
import clsx from "clsx";

import { Source } from "@/components/atoms/media/Source";
import { type SourceProps } from "@/components/atoms/media/Source";
import { type CommonProps } from "@/types";
import {
  cloudinarySource,
  contentfulSource,
  datasetFlag,
} from "@/utils/helpers";
import { containerCSSVars, createId, setCSSVariables } from "@/utils/helpers";

import styles from "./Picture.module.css";

const ref = "picture";

interface PictureProps
  extends Partial<CommonProps>,
    Partial<Pick<HTMLElement, "title">> {
  active?: boolean;
  image: ImageProps;
  index?: number;
}

function defaultZoom(variant: ImageVariantEnum) {
  switch (variant) {
    case ImageVariantEnum.CARD:
    case ImageVariantEnum.GRID:
    case ImageVariantEnum.PRIMARY:
    case ImageVariantEnum.SEARCH:
    case ImageVariantEnum.SECONDARY:
      return true;

    default:
      return false;
  }
}

function sources(image: ImageProps) {
  const imageMetrics = IMAGES_METRICS[image.variant || ImageVariantEnum.NONE];

  const urls = Object.entries(imageMetrics)
    .reverse()
    .flatMap(([mediaWidth, metrics]) => {
      return image.provider === ImageProviderEnum.CLOUDINARY
        ? cloudinarySource(image.src, +mediaWidth, metrics)
        : contentfulSource(image.src, +mediaWidth, metrics);
    });

  urls.pop();

  return urls;
}

function usePicture(props: PictureProps) {
  const image = { title: props.title, ...props.image };
  const { variant = ImageVariantEnum.NONE } = image;
  const className = clsx(
    styles[ref],
    defaultZoom(variant) && "zoom",
    props.className,
  );
  const { id = createId(ref), index } = props;
  let sourcesArray: Array<SourceProps> = [];
  const vars = containerCSSVars(useWindowSize(), image);

  if (image.src && image.provider !== ImageProviderEnum.BUCKET) {
    sourcesArray = sources(image);
  }

  let dataType;

  if (image.variant !== ImageVariantEnum.NONE) {
    dataType = image.variant;
  }

  return {
    active: !!props.active,
    className,
    dataType,
    id,
    image,
    index,
    sources: sourcesArray,
    vars: setCSSVariables(vars),
  };
}

function Picture(props: PictureProps) {
  const { active, className, dataType, id, image, index, sources, vars } =
    usePicture(props);

  if (!props.image.src) {
    return null;
  }

  return (
    <picture
      className={className}
      id={id}
      style={vars}
      data-active={datasetFlag(active)}
      data-index={index ?? null}
      data-type={dataType}
    >
      {sources.map((source, index) => (
        <Source key={`${id}_source_${index}`} {...source} />
      ))}
      <Image {...image} />
    </picture>
  );
}

export default Picture;
export { Picture, type PictureProps };
